<script setup>
import { ref, inject } from 'vue';
import { useMemberProfileStore } from '@/stores/memberProfile';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';
import { useDateFormatter } from '@/composables/dateFormatter';
import { router } from '@inertiajs/vue3';
import { capitalizeWords } from '@/utils/helper';

const { search, destroy, archiveOrActivateProfile, hasActiveProfile } = useMemberProfileStore();
const { all } = storeToRefs(useMemberProfileStore());
const { formatDateTime } = useDateFormatter();

const params = ref({
    term: '',
    page: 1,
    sort_by: null,
    per_page: 10,
    status: null,
});

const searchProfiles = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchProfiles();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchProfiles();
};

const remove = async (member) => {
    const confirm = await modals.show('ConfirmationModal', {
        title: trans('label.confirm_delete'),
        prompt: trans('label.are_you_sure_delete'),
    });

    if (confirm) {
        await destroy({ hash: member.hash });
        searchProfiles();
    }
};

const openModal = (modal, mode = 'Create', profile = null) => {
    modals.show(modal, {
        props: profile ? {
            profile: profile,
            mode: mode
        } : {
            mode: mode,
        }
    }).then(() => {
        searchProfiles();
    });
};

const access = async (item) => {
    router.visit(`/profile-details/${item.hash}`);
};

const handleCreate = async () => {
    const { has_active } = await hasActiveProfile();
    
    if (has_active) {
        await modals.show('AlertModal', {
            props: {
                title: trans('label.warning'),
                prompt: trans('label.active_profile_exists'),
            },
        });

        return;
    }

    openModal('MemberProfilesModal');
};

const archiveOrActivate = async (profile) => {
    // For Active Profile
    if (profile.is_active) {
        const confirm = await modals.show('AlertModal', {
            props: {
                title: trans('label.confirm_archive'),
                prompt: trans('label.are_you_sure_archive'),
            },
        });

        if (!confirm) {
            return;
        }

        await archiveOrActivateProfile({ hash: profile.hash });
        searchProfiles();
        return;
    }

    // For Inactive Profile
    const confirm=  await modals.show('AlertModal', {
        props: {
            title: trans('label.confirm_activate'),
            prompt: trans('label.are_you_sure_activate'),
        },
    });

    if (!confirm) {
        return;
    }

    try {
        await archiveOrActivateProfile({ hash: profile.hash });
        searchProfiles();
    } catch (error) {
        // handled toast via backend
    }
};

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.management_title',{ 'what' : 'Member Profiles' }) }}</template>
    <template #actions>
        <StyledButton @click="handleCreate">
            <FontAwesomeIcon icon="plus" class="mr-1" /> {{ $t('label.create') }}
        </StyledButton>
    </template>
    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchProfiles"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.creation_date'), key: 'created_at', value: i => formatDateTime(i.created_at), sortable: false },
            { title: $t('label.organization'), key: 'organization', value: i => i.organization.name, sortable: false },
            { title: $t('label.sport'), key: 'sport', value: i => capitalizeWords(i.sport.name), sortable: false },
            { title: $t('label.is_active'), key: 'is_active', value: i => (i.is_active ? $t('label.active') : $t('label.inactive')), sortable: false },
        ]"
        :actions="[
            {
                title: $t('label.edit'),
                icon: 'mdi-pencil',
                action: (item) => openModal('MemberProfilesModal', 'Edit', item),
            },
            {
                title: $t('label.delete'),
                icon: 'mdi-delete',
                action: (item) => remove(item),
            },
            {
                title: (item) => (item.is_active ? $t('label.archive') : $t('label.activate')),
                icon: 'mdi-archive-sync',
                action: (item) => archiveOrActivate(item),
            },
            {
                title: $t('label.access'),
                icon: 'mdi-login',
                action: (item) => access(item),
            },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
